<template>
    <div>
        <!-- catch data drift due to multi-tab/session -->
        <rc-stale-session-dialog
            v-model="show_state_changed_dialog"
            title="Message State Changed"
            message="This message has been updated since you loaded the page. Please reload the page before continuing."
            :persistent="false"
        />

        <!-- catchall to cap at MAX_POLL_DURATION_MS of polling -->
        <rc-stale-session-dialog v-model="show_timeout_dialog" :persistent="false" />
    </div>
</template>

<script>
import { MESSAGE_STATES } from '@/js/bulk/bulk-states.js';

export default {
    name: 'BulkMessageStateMonitor',

    props: {
        bulk_message_id: {
            type: String,
            required: true,
        },

        current_state: {
            type: String,
            default: null,
        },

        current_scheduled_send_time: {
            default: null,
        },
    },

    data() {
        return {
            poll_interval: null,
            poll_count: 0,
            show_state_changed_dialog: false,
            show_timeout_dialog: false,
            suppress_poll_until: null,
        }
    },

    methods: {
        // group message states into "buckets" that represent different UI flows
        getStateGroup(state) {
            if (state === MESSAGE_STATES.EDIT) return MESSAGE_STATES.EDIT;
            if ([MESSAGE_STATES.PREPARE, MESSAGE_STATES.REVIEW].includes(state)) return MESSAGE_STATES.REVIEW;
            if ([MESSAGE_STATES.SEND, MESSAGE_STATES.PAUSED, MESSAGE_STATES.CANCELLED, MESSAGE_STATES.FINISHED].includes(state)) return MESSAGE_STATES.SEND;
            return null;
        },

        startPolling() {
            if (this.poll_interval != null) return;
            
            const POLL_INTERVAL_MS = 3000;
            const MAX_POLL_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours
            const MAX_POLL_COUNT = Math.floor(MAX_POLL_DURATION_MS / POLL_INTERVAL_MS);
            
            this.poll_count = 0;
            
            this.poll_interval = window.setInterval(() => {
                this.poll_count++;

                if (this.poll_count >= MAX_POLL_COUNT) {
                    this.show_timeout_dialog = true;
                    this.stopPolling();
                    return;
                }

                this.poll();
            }, POLL_INTERVAL_MS);
        },

        stopPolling() {
            if (this.poll_interval != null) {
                window.clearInterval(this.poll_interval);
                this.poll_interval = null;
            }
        },

        suppressPollTemporarily() {
            const SUPPRESSION_DURATION_MS = 10000; // 10s
            this.suppress_poll_until = Date.now() + SUPPRESSION_DURATION_MS;
        },

        isPollingSuppressed() {
            return this.suppress_poll_until != null && Date.now() < this.suppress_poll_until;
        },

        poll() {
            if (this.current_state == null) return;
            if (this.isPollingSuppressed()) return;

            this.$rc.rest({
                spinner: false,
                endpoint: 'GET: /api/v1/bulk/messages/:bulk_message_id',
                params: { bulk_message_id: this.bulk_message_id },
                on_success: (new_data) => this.handlePollResult(new_data),
                on_error: () => {},
            });
        },

        handlePollResult(new_data) {
            if (this.isPollingSuppressed()) return;

            const DIALOG_WAIT_MS = 500;
            const current_group = this.getStateGroup(this.current_state);
            const new_group = this.getStateGroup(new_data.state);

            const state_group_changed = current_group !== new_group;
            const scheduled_in_another_session = this.current_scheduled_send_time == null && new_data.scheduled_send_time != null;

            if (state_group_changed || scheduled_in_another_session) {
                this.stopPolling();

                // slight delay to let UI thread finish before showing dialog
                // avoids scenario where dialog shows when current user is the one making the change
                setTimeout(() => {
                    if (this.isPollingSuppressed()) return;
                    this.show_state_changed_dialog = true;
                }, DIALOG_WAIT_MS);
                return;
            }

            if ([MESSAGE_STATES.FINISHED, MESSAGE_STATES.CANCELLED].includes(new_data.state)) {
                this.stopPolling();
            }
        },

        // used by parent to check if polling has timed out
        isTimedOut() {
            return this.show_timeout_dialog;
        },
    },

    watch: {
        current_state(new_state, old_state) {
            if (old_state != null && new_state !== old_state) {
                this.suppressPollTemporarily();
            }
        },
    },

    mounted() {
        this.startPolling();
    },

    beforeDestroy() {
        this.stopPolling();
    },
}
</script>